import React from "react";
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { BASE_URL } from "../services/config";
import { Card, CardBody, Input, Button } from "@heroui/react";
import api from "../services/api";
import { motion } from "framer-motion";
import { Icon } from "@iconify/react";
import { addToast } from "@heroui/react";

export const RegisterPage: React.FC = () => {
  const location = useLocation();
  const [username, setUsername] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [name, setName] = React.useState("");
  const [phone, setPhone] = React.useState("");
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [isPasswordVisible, setIsPasswordVisible] = React.useState(false);

  const togglePasswordVisibility = () => setIsPasswordVisible(!isPasswordVisible);

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!name.trim()) {
      setError("Пожалуйста, введите ваше имя");
      return;
    }

    if (!username.trim()) {
      setError("Пожалуйста, введите ваш логин");
      return;
    }

    if (password.length < 8) {
      setError("Пароль должен содержать не менее 8 символов");
      return;
    }

    setIsSubmitting(true);
    try {
      const parts = name.trim().split(/\s+/);
      const first_name = parts.length > 0 ? parts[0] : undefined;
      const last_name = parts.length > 1 ? parts.slice(1).join(' ') : undefined;

      const payload: any = {
        username: username.trim(),
        password: password,
      };
      if (first_name) payload.first_name = first_name;
      if (last_name) payload.last_name = last_name;

      await api.post('/users/register/', payload, { headers: { 'Content-Type': 'application/json' } });

      addToast({ title: 'Регистрация завершена', description: 'На указанный email отправлено письмо для подтверждения. Пожалуйста, подтвердите регистрацию перед входом в систему.', color: 'success' });
      window.location.href = '/register/confirmation';
    } catch (err: any) {
      setError(err?.response?.data?.message || "Ошибка при регистрации. Возможно, пользователь с таким логином уже существует.");
      console.error("Ошибка регистрации:", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="py-24 bg-background">
      <Helmet>
        <title>Регистрация — SAFONOVA</title>
        <meta name="description" content="Регистрация нового пользователя в интернет-магазине SAFONOVA." />
        <link rel="canonical" href={`${BASE_URL || ''}${location?.pathname || '/register'}`} />
        <meta property="og:title" content="Регистрация — SAFONOVA" />
        <meta property="og:description" content="Создайте аккаунт для управления заказами и профилем в интернет-магазине SAFONOVA." />
        <meta property="og:url" content={`${BASE_URL || ''}${location?.pathname || '/register'}`} />
      </Helmet>
      <div className="container mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="max-w-md mx-auto"
        >
          <Card className="border border-divider">
            <CardBody className="p-8">
              <h1 className="text-2xl font-light text-center mb-6">
                Регистрация в <span className="font-medium">личном кабинете</span>
              </h1>

              {error && (
                <div className="bg-danger-50 text-danger p-3 mb-6 text-sm">
                  {error}
                </div>
              )}

              <form onSubmit={handleRegister} className="space-y-4">
                <Input
                  type="text"
                  label="Имя и фамилия"
                  placeholder="Введите ваше имя"
                  value={name}
                  onValueChange={setName}
                  variant="bordered"
                  isRequired
                  autoComplete="name"
                />

                <Input
                  type="text"
                  label="Email"
                  placeholder="Введите ваш email"
                  value={username}
                  onValueChange={setUsername}
                  variant="bordered"
                  isRequired
                  autoComplete="username"
                />

                <Input
                  type="tel"
                  label="Телефон"
                  placeholder="+7 (___) ___-__-__"
                  value={phone}
                  onValueChange={setPhone}
                  variant="bordered"
                  autoComplete="tel"
                />

                <Input
                  type={isPasswordVisible ? "text" : "password"}
                  label="Пароль"
                  placeholder="Минимум 8 символов"
                  value={password}
                  onValueChange={setPassword}
                  variant="bordered"
                  isRequired
                  autoComplete="new-password"
                  endContent={
                    <button
                      type="button"
                      onClick={togglePasswordVisibility}
                      className="focus:outline-none"
                    >
                      {isPasswordVisible ? (
                        <Icon icon="lucide:eye-off" className="text-foreground/60" width={16} />
                      ) : (
                        <Icon icon="lucide:eye" className="text-foreground/60" width={16} />
                      )}
                    </button>
                  }
                />

                <p className="text-xs text-foreground/70">
                  Регистрируясь, вы соглашаетесь с
                  <a href="/terms" className="text-primary ml-1">Условиями использования</a> и
                  <a href="/privacy" className="text-primary ml-1">Политикой конфиденциальности</a>
                </p>

                <Button
                  type="submit"
                  color="primary"
                  className="w-full"
                  isLoading={isSubmitting}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Регистрация..." : "Зарегистрироваться"}
                </Button>

                <p className="text-xs text-foreground/70 text-center mt-4">
                  Уже есть аккаунт?{' '}
                  <a href="/auth" className="text-primary">Войдите</a>
                </p>
              </form>
            </CardBody>
          </Card>
        </motion.div>
      </div>
    </section>
  );
};

export default RegisterPage;
